/**
 * Shared contract vocabulary (spec §11).
 *
 * Every operation is tenant-scoped; every write is idempotent on a client
 * key; requests are strict — unknown fields are refused at the door, not
 * passed through to be someone else's problem.
 */

import { z } from "zod";

/**
 * An id. Unbounded HERE on purpose: this is the RESPONSE vocabulary too, and
 * the kernel validates every answer against its contract, so a cap on `zId`
 * would refuse to read a stored record. Every REQUEST id is bounded instead —
 * `build()` swaps this exact instance for `zDoorId` as each request is built
 * (B1110), so no door takes an id past `ID_MAX`.
 */
export const zId = z.string().min(1);
/**
 * B1110 — an id at a door. An id becomes a DynamoDB key (sort key ≤ 1,024
 * bytes, partition key ≤ 2,048), so an oversized one failed as a 500
 * ValidationException in the store; 256 characters fit a sort key with room
 * for its prefix even at four UTF-8 bytes a character.
 */
export const ID_MAX = 256;
export const zDoorId = zId.max(ID_MAX);
/**
 * B1112 — the most of one thing a single line, refund line or party entry can
 * name. `.int()` takes any integer-valued double, so `cart.add` with
 * `quantity: 1e17` reached the domain's `multiply` and answered 500; ten
 * thousand is far past any one cart line and keeps quantity × a unit price
 * well inside a safe integer.
 */
export const LINE_QUANTITY_MAX = 10_000;
/**
 * B1111 — the bounds a refinement carries, kept beside the schema rather than
 * in its description so the generated docs say them in their own words
 * (`maxProperties`, `x-transact-maxJsonLength` — json-schema.ts) and the
 * walls can read them. Only schemas made by the two helpers below are here.
 */
export interface DoorBound {
  readonly maxEntries?: number;
  readonly maxJson?: number;
  /** B1115 — a `YYYY-MM-DD` that names a real calendar day (JSON Schema's `format: date`). */
  readonly calendarDay?: true;
}
const DOOR_BOUNDS = new WeakMap<z.ZodTypeAny, DoorBound>();
export function doorBound(schema: z.ZodTypeAny): DoorBound | undefined {
  return DOOR_BOUNDS.get(schema);
}
/** B1111 — a record at a door counts its entries (refused at the record, in words). */
export function zCountedRecord<K extends z.ZodString, V extends z.ZodTypeAny>(key: K, value: V, maxEntries: number) {
  const s = z
    .record(key, value)
    .refine((r) => Object.keys(r).length <= maxEntries, { message: `At most ${String(maxEntries)} entries` });
  DOOR_BOUNDS.set(s, { maxEntries });
  return s;
}
/** B1111 — free-form JSON at a door is sized: at most `maxChars` characters once serialised. */
export function zJsonSized<T extends z.ZodTypeAny>(schema: T, maxChars: number) {
  const s = schema.refine((v) => JSON.stringify(v).length <= maxChars, {
    message: `At most ${String(maxChars)} characters as JSON`,
  });
  DOOR_BOUNDS.set(s, { maxJson: maxChars });
  return s;
}

/**
 * B1115 — the one `YYYY-MM-DD` shape every request day is written in (§4.6).
 * The regex alone took `2026-13-01` and `2026-02-31`: `schedule.create`
 * stored a schedule from month thirteen that materialized nothing, and
 * `Date.parse` read Feb 31 as March 3.
 */
export const CALENDAR_DAY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
const DAYS_IN_MONTH = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31] as const;
/** B1115 — pure arithmetic (no Date): the proleptic Gregorian calendar's leap rule. */
export function isCalendarDay(value: string): boolean {
  if (!CALENDAR_DAY_PATTERN.test(value)) return false;
  const y = Number(value.slice(0, 4));
  const m = Number(value.slice(5, 7));
  const d = Number(value.slice(8, 10));
  if (m < 1 || m > 12 || d < 1) return false;
  const leap = (y % 4 === 0 && y % 100 !== 0) || y % 400 === 0;
  return d <= (m === 2 && leap ? 29 : DAYS_IN_MONTH[m - 1]!);
}
/** B1115 — a day string at a door names a real day (refused at the field, in words). */
export function zCalendarDay<T extends z.ZodTypeAny>(schema: T) {
  const s = schema.refine((v) => typeof v !== "string" || isCalendarDay(v), { message: "Not a real calendar day" });
  DOOR_BOUNDS.set(s, { calendarDay: true });
  return s;
}
const isDayString = (def: Record<string, unknown>): boolean =>
  (def["checks"] as { kind: string; regex?: RegExp }[]).some((c) => c.kind === "regex" && c.regex?.source === CALENDAR_DAY_PATTERN.source);

/**
 * B1116 — where a hosted card page sends the payer back. `membership.buy` (a
 * CUSTOMER door), `membership.renew` and `membership.autorenew.set` took
 * `z.string().url()` alone, so `javascript:`, `data:`, `ftp:` and plain
 * `http://` pages reached the gateway as the success/cancel address. The rule
 * is Connect's (`validateConnectReturnUrls`): https, plain http only on
 * localhost for a page under development. Which HOSTS may be named is B1116
 * leg (b), class A. A pattern rather than a refinement, so the JSON Schema
 * (and the MCP membrane) say it.
 */
export const RETURN_URL_PATTERN = /^(?:https:\/\/[^\s/?#@]+[^\s]*|http:\/\/(?:localhost|127\.0\.0\.1)(?::\d{1,5})?(?:[/?#][^\s]*)?)$/;
export const RETURN_URL_MAX = 2048;
export function isReturnUrl(value: string): boolean {
  return value.length <= RETURN_URL_MAX && RETURN_URL_PATTERN.test(value);
}
/** B1116 — the pattern first, so a refused address reads the rule rather than "Invalid url". */
export const zReturnUrl = z
  .string()
  .max(RETURN_URL_MAX)
  .regex(RETURN_URL_PATTERN, { message: "Use a full https:// address (plain http:// only on localhost)" })
  .url();

/** B1110 — an opaque page cursor (a DynamoDB key, encoded). */
export const CURSOR_MAX = 4096;
/** B1110 — a signed token presented at a door (a ticket QR, a session link) — the bound the other token doors already carry. */
export const TOKEN_MAX = 4096;
/** B1110 — free text typed into a search box. */
export const SEARCH_QUERY_MAX = 200;
/** B1110 — a short filter value matched against stored rows (a status, a kind, a code). */
export const FILTER_TEXT_MAX = 120;
/** B1110 — a registry operation's name ("membership.guestpass.spend" is 26). */
export const OPERATION_NAME_MAX = 80;
/** B1110 — a URL typed at a door. */
export const URL_MAX = 2048;
/** B1110 — an address that NAMES a stored record (erase, look up): the widest any door ever admitted (zRecipient's 320). */
export const STORED_EMAIL_MAX = 320;
/** B1098 — a time zone at a WRITE door: an IANA name (the longest is 32 characters). Reads keep loading what is stored; the domain still judges whether the zone exists. */
export const TIMEZONE_MAX = 64;
export const zClientKey = z.string().min(8).max(128);
/**
 * B608 — where mail is SENT: a typed re-delivery destination is an email or
 * it is refused at the door, the same bound every other recipient field
 * carries. One home on purpose: the admin field judge reads THIS schema, so
 * the field and the wire cannot drift (B607's shape).
 */
export const zRecipient = z.string().email().max(320);
/**
 * B1059 — a typed buyer address's cap: 254, the SMTP path limit (RFC 5321
 * §4.5.3.1.3 less the angle brackets). Named so a storefront door says it
 * at its field instead of the server's parse refusing it after the press.
 */
export const EMAIL_MAX = 254;
/**
 * B689 (INCIDENT TRA-683) — an instant is one a `Date` can hold: ±8.64e15 ms
 * (ECMA-262 §21.4.1.1), not every safe integer. Past that, `new Date(ms)` is
 * Invalid and the first `toISOString` throws a RangeError inside a handler —
 * report.run answered 500 to an eval loop's out-of-range `to`. Refused at the
 * door instead, for every op whose instants become dates.
 */
export const DATE_MAX_EPOCH_MS = 8_640_000_000_000_000;
export const zEpochMs = z.number().int().min(-DATE_MAX_EPOCH_MS).max(DATE_MAX_EPOCH_MS);

/** Integer minor units only — no floats, no exceptions. */
export const zMoney = z
  .object({ amount: z.number().int().safe(), currency: z.string().regex(/^[A-Z]{3}$/) })
  .strict();
export const zNonNegMoney = z
  .object({ amount: z.number().int().safe().nonnegative(), currency: z.string().regex(/^[A-Z]{3}$/) })
  .strict();

/**
 * B20b(h): "public" is the third door — reachable with NO principal (the
 * guest-session mint is how a buyer gets their first credential, so it
 * cannot demand one). Public ops are tenant-scoped, carry no clientKey
 * (each mint is fresh), and their wall is the rate limit + audit row, not
 * a scope.
 */
export type OperationKind = "read" | "write" | "internal" | "public";
/** §11: a human in the loop, named where the spec names one. */
export type Gate = "human" | "admin" | "manager";

/**
 * B25b — the §11 audience column. The permission column's parentheticals
 * ("POS roles", "MANAGER at POS", "ADMIN approves") narrow WHO may call an
 * operation beyond the scope it requires; scopes alone under-specify §11
 * (the payment.record_external finding). Kinds may be role-qualified
 * (STAFF:ADMIN) or device-type-qualified (DEVICE:VALIDATOR); a bare STAFF
 * admits any staff role that also clears the scope wall. DEVICE:TERMINAL is
 * deliberately absent from the vocabulary — §9.1: the provider's terminal
 * fleet never calls the membrane. CAPABILITY (B41) admits saved-capability
 * runs executing under their frozen grant set; no Phase-1 §11 op lists it
 * yet — B42's runtime opens doors deliberately, one op at a time.
 */
export type PrincipalAllowance =
  /** B631 (a) — a tenant API key; declared only on the buyer-path operations its scopes can reach. */
  | "API_KEY"
  | "CAPABILITY"
  /** B94 — the platform's own seat. Only ever declared on a `platform` operation. */
  | "OPERATOR"
  | "OPERATOR:ROOT"
  | "OPERATOR:OPERATOR"
  | "OPERATOR:SALES"
  | "OPERATOR:SUPPORT"
  | "CUSTOMER"
  | "RESELLER"
  | "STAFF"
  | "STAFF:ADMIN"
  | "STAFF:MANAGER"
  | "STAFF:SELLER"
  | "STAFF:VALIDATOR"
  | "DEVICE:VALIDATOR"
  | "DEVICE:KIOSK"
  | "DEVICE:PRINTER";

export interface OperationContract<
  Req extends z.ZodTypeAny = z.ZodTypeAny,
  Res extends z.ZodTypeAny = z.ZodTypeAny,
> {
  readonly name: string;
  readonly kind: OperationKind;
  readonly permission: string;
  /** B25b — who may call: the declarative audience the kernel enforces. */
  readonly principals: readonly PrincipalAllowance[];
  readonly gate?: Gate;
  /**
   * B413 — a public door with NO tenant either: the caller is an un-paired
   * device that holds nothing (device.announce). The authorizer and the
   * kernel skip the claimed-tenant wall for exactly these ops; everything
   * else keeps it.
   */
  readonly tenantless?: true;
  /**
   * B94 — the platform door (RULING 2026-09-21, Pathik: a console of our own).
   * The ONE way an OPERATOR principal reaches anything: an operation opts in
   * by declaring which shape of crossing it is, and declares the operator
   * roles it admits in `principals`. Absent — which is every §11 operation —
   * means no operator may call it, enforced by the Inv 10 wall that already
   * exists (an operator holds the platform sentinel, which no request claims).
   *
   * - "fleet": the ask is about the platform, not a tenant (tenant.list).
   *   No claimed tenant; audited on the platform's own partition.
   * - "tenant": the ask names ONE tenant and the operator's `custom:tenants`
   *   must admit it. The audit row is written in THAT TENANT's partition, so
   *   a business reading its own trail sees who looked — which is the whole
   *   of the 2026-09-21 09:59 PDT audit condition.
   *
   * A platform operation declares no `gate`: the §11 gates name staff roles
   * (`gatePasses` refuses every non-STAFF principal by construction), so a
   * gated platform door would be a door that never opens. Walled in
   * `packages/contracts/test/b94-platform-ops.test.ts`.
   */
  readonly platform?: "fleet" | "tenant";
  /**
   * B25c/B25d — the §11 owner rule (RULINGS 2026-07-15 esc 2, 2026-07-22).
   * "customer" marks a CUSTOMER-audience read OR write over customer-owned
   * records: the kernel arms an ownership fence for CUSTOMER principals and
   * the handler MUST discharge it — a foreign-owned id answers exactly like
   * an absent one (uniform 404/empty), never a 200, and never a state-wall
   * refusal that leaks existence. STAFF/RESELLER/DEVICE act across the
   * tenant unaffected. On writes the undischarged-fence 500 records no
   * outcome (the idempotency reservation releases).
   */
  readonly owner?: "customer";
  /**
   * B469 — the tenant entitlement this operation rides behind (docs/
   * TENANT-CUSTOMIZATION.md). Declared as DATA so the kernel's op gate is
   * the ONE seam that checks it; the key must exist in the domain's
   * TENANT_FEATURE_REGISTRY (walled). Absent = the op is for every tenant.
   */
  readonly entitlement?: string;
  readonly request: Req;
  readonly response: Res;
  /** Living documentation: a canonical parsing example per direction. */
  readonly exampleRequest: unknown;
  readonly exampleResponse: unknown;
}

export type RequestOf<C extends OperationContract> = z.infer<C["request"]>;
export type ResponseOf<C extends OperationContract> = z.infer<C["response"]>;

interface BaseOpts<Res extends z.ZodTypeAny> {
  readonly name: string;
  readonly permission: string;
  readonly principals: readonly PrincipalAllowance[];
  readonly fields: z.ZodRawShape;
  readonly response: Res;
  readonly gate?: Gate;
  readonly owner?: "customer";
  readonly entitlement?: string;
  readonly exampleRequest: Record<string, unknown>;
  readonly exampleResponse: unknown;
}

const EXAMPLE_TENANT = "tenant-1";
const EXAMPLE_KEY = "client-key-0001";

/**
 * B1110 — every id in a REQUEST is `zDoorId`; B1112 — and every integer in a
 * REQUEST carries a min and a max (±MAX_SAFE_INTEGER where it named none);
 * B1115 — and every `YYYY-MM-DD` names a real calendar day.
 * Rebuilds only the wrappers that
 * hold the shared `zId` instance (each rebuilt from its own def, so `.strict()`,
 * refinements, `.max()` on a list and defaults all survive); every schema that
 * holds no `zId` is returned as the same object. A schema kind not walked here
 * keeps its ids unbounded — the B1110 wall names any that reach a door.
 */
function boundIds(schema: z.ZodTypeAny): z.ZodTypeAny {
  if (schema === zId) return zDoorId;
  const s = schema as z.ZodTypeAny & { _def: Record<string, unknown> };
  const def = s._def as { typeName: string } & Record<string, unknown>;
  const re = <T>(Ctor: new (d: never) => T, d: Record<string, unknown>): T => new Ctor({ ...def, ...d } as never);
  switch (def.typeName) {
    case "ZodOptional":
    case "ZodNullable":
    case "ZodDefault": {
      const inner = boundIds(def.innerType as z.ZodTypeAny);
      if (inner === def.innerType) return schema;
      const Ctor = def.typeName === "ZodOptional" ? z.ZodOptional : def.typeName === "ZodNullable" ? z.ZodNullable : z.ZodDefault;
      return re(Ctor as never, { innerType: inner }) as z.ZodTypeAny;
    }
    case "ZodArray": {
      const inner = boundIds(def.type as z.ZodTypeAny);
      return inner === def.type ? schema : re(z.ZodArray, { type: inner });
    }
    case "ZodEffects": {
      const inner = boundIds(def.schema as z.ZodTypeAny);
      if (inner === def.schema) return schema;
      const rebuilt = re(z.ZodEffects, { schema: inner });
      const bound = DOOR_BOUNDS.get(schema);
      if (bound !== undefined) DOOR_BOUNDS.set(rebuilt, bound);
      return rebuilt;
    }
    case "ZodRecord": {
      const inner = boundIds(def.valueType as z.ZodTypeAny);
      return inner === def.valueType ? schema : re(z.ZodRecord, { valueType: inner });
    }
    case "ZodString":
      // B1115 — a day-shaped string names a real day (the refinement is a
      // door bound, so json-schema.ts and the MCP membrane still translate it).
      return isDayString(def) ? zCalendarDay(schema) : schema;
    case "ZodNumber": {
      // B1112 — an integer with no bound of its own takes ±MAX_SAFE_INTEGER
      // (zMoney's `.safe()`), so an unsafe one is a 400 here, never a domain
      // RangeError answering 500. Floats are left as they are: JSON carries no
      // non-finite number, and the request floats are ranged where they live.
      const checks = def.checks as { kind: string }[];
      if (!checks.some((c) => c.kind === "int")) return schema;
      const hasMin = checks.some((c) => c.kind === "min");
      const hasMax = checks.some((c) => c.kind === "max");
      if (hasMin && hasMax) return schema;
      return re(z.ZodNumber, {
        checks: [
          ...checks,
          ...(hasMin ? [] : [{ kind: "min", value: Number.MIN_SAFE_INTEGER, inclusive: true }]),
          ...(hasMax ? [] : [{ kind: "max", value: Number.MAX_SAFE_INTEGER, inclusive: true }]),
        ],
      });
    }
    case "ZodObject": {
      const shape = (schema as z.AnyZodObject).shape as z.ZodRawShape;
      const next = boundRequestIds(shape);
      return next === shape ? schema : re(z.ZodObject, { shape: () => next });
    }
    case "ZodUnion": {
      const options = def.options as z.ZodTypeAny[];
      const next = options.map(boundIds);
      return next.every((o, i) => o === options[i]) ? schema : re(z.ZodUnion, { options: next });
    }
    case "ZodDiscriminatedUnion": {
      const options = def.options as z.AnyZodObject[];
      const next = options.map(boundIds) as z.AnyZodObject[];
      if (next.every((o, i) => o === options[i])) return schema;
      const optionsMap = new Map([...(def.optionsMap as Map<unknown, z.AnyZodObject>)].map(([k, o]) => [k, next[options.indexOf(o)]!] as const));
      return re(z.ZodDiscriminatedUnion, { options: next, optionsMap });
    }
    default:
      return schema;
  }
}

function boundRequestIds(shape: z.ZodRawShape): z.ZodRawShape {
  let changed = false;
  const next: Record<string, z.ZodTypeAny> = {};
  for (const [k, v] of Object.entries(shape)) {
    next[k] = boundIds(v);
    if (next[k] !== v) changed = true;
  }
  return changed ? next : shape;
}

function build<Res extends z.ZodTypeAny>(
  kind: OperationKind,
  shape: z.ZodRawShape,
  opts: BaseOpts<Res>,
  exampleExtras: Record<string, unknown>,
): OperationContract<z.ZodTypeAny, Res> {
  if ((kind === "internal" || kind === "public") && opts.principals.length > 0)
    throw new Error(`${kind} operation ${opts.name} cannot declare an external audience`);
  if (kind !== "internal" && kind !== "public" && opts.principals.length === 0)
    throw new Error(`Operation ${opts.name} must declare its audience (B25b)`);
  if (opts.owner !== undefined && !opts.principals.includes("CUSTOMER"))
    throw new Error(`Operation ${opts.name} declares an owner rule but admits no CUSTOMER (B25c)`);
  const contract: OperationContract<z.ZodTypeAny, Res> = {
    name: opts.name,
    kind,
    permission: opts.permission,
    principals: Object.freeze([...opts.principals]),
    ...(opts.gate !== undefined ? { gate: opts.gate } : {}),
    ...(opts.owner !== undefined ? { owner: opts.owner } : {}),
    ...(opts.entitlement !== undefined ? { entitlement: opts.entitlement } : {}),
    request: z.object(boundRequestIds(shape)).strict(),
    response: opts.response,
    exampleRequest: { ...exampleExtras, ...opts.exampleRequest },
    exampleResponse: opts.exampleResponse,
  };
  return contract;
}

/** A read: tenant-scoped, no client key — reads have nothing to replay. */
export function readOp<Res extends z.ZodTypeAny>(opts: BaseOpts<Res>) {
  return build("read", { tenantId: zId, ...opts.fields }, opts, { tenantId: EXAMPLE_TENANT });
}

/** A write: tenant-scoped and idempotent on a client key (Inv 4). */
export function writeOp<Res extends z.ZodTypeAny>(opts: BaseOpts<Res>) {
  return build("write", { tenantId: zId, clientKey: zClientKey, ...opts.fields }, opts, {
    tenantId: EXAMPLE_TENANT,
    clientKey: EXAMPLE_KEY,
  });
}

/**
 * B20b(h): the public door — tenant-scoped, principal-less, no client key.
 * The kernel skips the principal walls for this kind; the handler's rate
 * limit and the membrane's audit row are the walls.
 */
export function publicOp<Res extends z.ZodTypeAny>(opts: Omit<BaseOpts<Res>, "principals">) {
  return build("public", { tenantId: zId, ...opts.fields }, { ...opts, principals: [] }, { tenantId: EXAMPLE_TENANT });
}

/**
 * B94 — a PLATFORM read across the fleet (tenant.list). No tenantId: the
 * ask is about the platform, not about one business, and the operator's
 * own `custom:tenants` scope is what bounds the answer — applied in the
 * handler, over rows the directory already holds. Only OPERATOR principals
 * reach it (walled in `test/b94-platform-ops.test.ts`).
 */
export function fleetReadOp<Res extends z.ZodTypeAny>(opts: BaseOpts<Res>) {
  const contract = build("read", { ...opts.fields }, opts, {});
  return Object.freeze({ ...contract, platform: "fleet" as const });
}

/**
 * B94 leg (c) — a PLATFORM write across the fleet (operator.users.*). No
 * tenantId — the record it writes belongs to the platform itself — but the
 * clientKey stays: a fleet write is as replayable as a tenant one (Inv 4;
 * the kernel partitions its idempotency row under the operator sentinel).
 */
export function fleetWriteOp<Res extends z.ZodTypeAny>(opts: BaseOpts<Res>) {
  const contract = build("write", { clientKey: zClientKey, ...opts.fields }, opts, { clientKey: EXAMPLE_KEY });
  return Object.freeze({ ...contract, platform: "fleet" as const });
}

/**
 * B94 — a PLATFORM read or write INTO one named tenant. The tenant is named
 * in the request as usual, but the wall is the operator's scope rather than
 * the Inv 10 equality, and the audit row lands in that tenant's own trail.
 */
export function tenantCrossingReadOp<Res extends z.ZodTypeAny>(opts: BaseOpts<Res>) {
  const contract = build("read", { tenantId: zId, ...opts.fields }, opts, { tenantId: EXAMPLE_TENANT });
  return Object.freeze({ ...contract, platform: "tenant" as const });
}

/**
 * B94 leg (d) — a PLATFORM write INTO one named tenant (`operator.login_as`):
 * the crossing wall of tenantCrossingReadOp, plus the client key every
 * write carries (Inv 4).
 */
export function tenantCrossingWriteOp<Res extends z.ZodTypeAny>(opts: BaseOpts<Res>) {
  const contract = build("write", { tenantId: zId, clientKey: zClientKey, ...opts.fields }, opts, {
    tenantId: EXAMPLE_TENANT,
    clientKey: EXAMPLE_KEY,
  });
  return Object.freeze({ ...contract, platform: "tenant" as const });
}

/**
 * B413 — the tenant-LESS public door: no principal AND no tenant. The one
 * caller shape is an un-paired device announcing its pairing code; the
 * tenant "arrives inside the enrolled credential" (validator config), so
 * demanding one here would demand what the caller cannot know. The
 * handler's rate wall is load-bearing — a tenantless door has no other.
 */
export function publicTenantlessOp<Res extends z.ZodTypeAny>(opts: Omit<BaseOpts<Res>, "principals">) {
  const contract = build("public", { ...opts.fields }, { ...opts, principals: [] }, {});
  return Object.freeze({ ...contract, tenantless: true as const });
}

/** Provider-facing: exactly-once on the provider's event id, never a client key. */
export function internalOp<Res extends z.ZodTypeAny>(opts: Omit<BaseOpts<Res>, "principals">) {
  return build("internal", { tenantId: zId, ...opts.fields }, { ...opts, principals: [] }, { tenantId: EXAMPLE_TENANT });
}

/**
 * B551 leg (b) — the reference id of a failed call: the failure body's
 * `requestId` (every ops-entry failure carries it), else the API Gateway
 * `apigw-requestid` header the ops API exposes (an answer the gateway made
 * itself — a throttle, a 503 — has no body of ours). Every app's API error
 * reads it here, so the rule lives once.
 */
export function wireRequestId(body: unknown, header: string | null | undefined): string | undefined {
  if (body !== null && typeof body === "object") {
    const id = (body as { requestId?: unknown }).requestId;
    if (typeof id === "string" && id !== "") return id.slice(0, 128);
  }
  return typeof header === "string" && header !== "" ? header.slice(0, 128) : undefined;
}
